/**
 * Client Profile Step 5 — trusts & companies fact-find (Phase 1a UI only).
 */
import React, { useState } from 'react';
import { PlusCircle, Trash2, AlertTriangle, Info } from 'lucide-react';
import { createEmptyStructure, validateStructureApportionment } from '../../constants/clientStructureTypes.ts';
import type { DraftStructure } from '../../constants/clientStructureTypes.ts';
import {
    CLIENT_STRUCTURE_TYPES,
    getApportionmentRemainderPercent,
} from '../../../../shared/src/calculation/constants/clientStructureTypes.ts';
import type { ClientStructure } from '../../../../shared/src/calculation/constants/clientStructureTypes.ts';
import { FormInput, FormSelect, FormRadioGroup, InfoIcon, CurrencyInput, PercentageInput } from '../../components/ui/index.tsx';
import { formatCurrency, toYesNo, fromYesNo } from '../../utils/index.ts';
import type { ApplicantVoice } from '../../utils/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { confirmAction } from '../../components/ui/notices.tsx';

/** Centrelink attribution help — plain language; not legal advice. */
const buildApportionmentTooltip = (voice: ApplicantVoice) =>
    `Centrelink often counts a family trust toward one person in full — not automatically half each between a couple. Use ${voice.pronounPossessive} Centrelink letter or ${voice.pronounPossessive} accountant's advice. If you're not sure, leave 0% until you confirm.`;

export interface ClientStructuresSectionProps {
    factFind: DraftFactFind;
    setFactFind: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    isPartnered: boolean;
    clientName: string;
    partnerName: string;
    showAdviserFields?: boolean;
    voice: ApplicantVoice;
}

const ClientStructuresSection = ({ factFind, setFactFind, isPartnered, clientName, partnerName, showAdviserFields = false, voice }: ClientStructuresSectionProps) => {
    const apportionmentTooltip = buildApportionmentTooltip(voice);
    const [apportionmentErrors, setApportionmentErrors] = useState<Record<string, string | undefined>>({});

    const structures = factFind.structures || [];
    const gateAnswer = factFind.structuresGateAnswer ?? false;
    const homeIsEntity = factFind.home?.ownership === 'other_entity';
    const homePrefill = homeIsEntity && !gateAnswer;

    const setGateAnswer = async (value: boolean) => {
        if (!value && homeIsEntity) {
            const proceed = await confirmAction(
                `On the previous step you said ${voice.possessive} home is owned by a trust or company. If you continue, we won't include that arrangement in the estimates until you update Step 4 or add the entity here.\n\nContinue without structures?`
            );
            if (!proceed) return;
        }
        setFactFind((prev) => ({
            ...prev,
            structuresGateAnswer: value,
            ...(!value ? { structures: [], home: { ...prev.home, entityId: '' } } : {}),
        }));
        if (!value) setApportionmentErrors({});
    };

    const updateStructure = (id: string | undefined, field: string, value: unknown) => {
        setFactFind((prev) => ({
            ...prev,
            structures: (prev.structures || []).map((s) => (s.id === id ? { ...s, [field]: value } : s)),
        }));
        if (field === 'apportionmentClient' || field === 'apportionmentPartner') {
            setApportionmentErrors((prev) => {
                const next = { ...prev };
                delete next[id || ''];
                return next;
            });
        }
    };

    const addStructure = () => {
        setFactFind((prev) => ({
            ...prev,
            structuresGateAnswer: true,
            structures: [...(prev.structures || []), createEmptyStructure()],
        }));
    };

    const removeStructure = async (id: string | undefined) => {
        const target = structures.find((s) => s.id === id);
        const label = target?.name?.trim() || 'this arrangement';
        if (!(await confirmAction(`Remove ${label}? Assets linked to it will need a new owner.`))) return;

        setFactFind((prev) => {
            const nextStructures = (prev.structures || []).filter((s) => s.id !== id);
            const nextAssets = { ...prev.assets };
            Object.keys(nextAssets).forEach((type) => {
                nextAssets[type] = (nextAssets[type] || []).map((asset) =>
                    asset.ownership === `entity:${id}` ? { ...asset, ownership: 'client' } : asset
                );
            });
            const nextHome =
                prev.home?.entityId === id
                    ? { ...prev.home, entityId: '', ownership: prev.home?.ownership === 'other_entity' ? 'client' : prev.home?.ownership }
                    : prev.home;
            return {
                ...prev,
                structures: nextStructures,
                assets: nextAssets,
                home: nextHome,
                structuresGateAnswer: nextStructures.length > 0 ? true : prev.structuresGateAnswer,
            };
        });
        setApportionmentErrors((prev) => {
            const next = { ...prev };
            delete next[id || ''];
            return next;
        });
    };

    const blurApportionment = (structure: DraftStructure) => {
        const result = validateStructureApportionment(structure, isPartnered);
        setApportionmentErrors((prev) => ({
            ...prev,
            [structure.id || '']: result.valid ? undefined : result.message,
        }));
    };

    const linkHomeEntity = (entityId: string) => {
        setFactFind((prev) => ({
            ...prev,
            home: { ...prev.home, entityId: entityId || '' },
        }));
    };

    const showStructuresBlock = gateAnswer === true;

    return (
        <fieldset className="space-y-4">
            <legend className="text-lg font-semibold text-gray-700 mb-2">Trusts & companies</legend>
            <p className="text-sm text-gray-600 mb-4">
                Some people hold investments or property through a family trust, company, or partnership — not in their own name.
            </p>

            {homePrefill && (
                <div className="flex gap-2 p-3 rounded-lg bg-teal-50 border border-teal-200 text-sm text-teal-900">
                    <Info className="shrink-0 mt-0.5" size={18} />
                    <span>You told us {voice.possessive} home is owned by a trust or company. Please add that arrangement below.</span>
                </div>
            )}

            <FormRadioGroup
                label={`Is any of ${voice.possessive} money or property held this way?`}
                name="structuresGateAnswer"
                value={toYesNo(gateAnswer)}
                onChange={(n, v) => setGateAnswer(fromYesNo(v))}
                options={[
                    {
                        label: voice.isThirdPerson
                            ? `No — everything is in ${voice.possessive} personal names`
                            : 'No — everything is in our personal names',
                        value: 'no',
                    },
                    {
                        label: voice.isThirdPerson
                            ? `Yes — ${voice.subject} ${voice.verb('have', 'has')} a trust, company, or similar`
                            : 'Yes — we have a trust, company, or similar',
                        value: 'yes',
                    },
                ]}
            />

            {showStructuresBlock && (
                <div className="space-y-6 pt-2">
                    <div>
                        <h4 className="text-md font-semibold text-gray-700 mb-2">{voice.Possessive} arrangements</h4>
                        {structures.length === 0 && (
                            <p className="text-sm text-gray-600 mb-3">
                                Add each trust, company, or partnership that holds assets for {voice.pronounPossessive} family.
                            </p>
                        )}
                        <div className="space-y-4">
                            {structures.map((structure) => {
                                const apportionmentError = apportionmentErrors[structure.id || ''];
                                const clientPct = parseFloat(String(structure.apportionmentClient)) || 0;
                                const partnerPct = parseFloat(String(structure.apportionmentPartner)) || 0;
                                const bothZero = clientPct === 0 && (!isPartnered || partnerPct === 0);

                                return (
                                    <div key={structure.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
                                        <div className="flex justify-between items-start gap-2">
                                            <p className="font-medium text-gray-800">
                                                {structure.name?.trim() || 'New arrangement'}
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => removeStructure(structure.id)}
                                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full"
                                                aria-label="Remove arrangement"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <FormInput
                                                label={`What ${voice.verb('do', 'does')} ${voice.subject} call this?`}
                                                name="name"
                                                value={structure.name}
                                                placeholder="e.g. Smith Family Trust"
                                                onChange={(n, v) => updateStructure(structure.id, n, v)}
                                            />
                                            <FormSelect
                                                label={
                                                    <span>
                                                        What type is it?{' '}
                                                        <InfoIcon text={`Choose the closest match. If you're unsure, pick Other and check with ${voice.pronounPossessive} accountant.`} />
                                                    </span>
                                                }
                                                name="type"
                                                value={structure.type || 'family_trust'}
                                                onChange={(n, v) => updateStructure(structure.id, n, v)}
                                            >
                                                {CLIENT_STRUCTURE_TYPES.map((t) => (
                                                    <option key={t.id} value={t.id}>
                                                        {t.label}
                                                    </option>
                                                ))}
                                            </FormSelect>
                                        </div>

                                        <div className="border-t border-gray-200 pt-4">
                                            <p className="text-sm font-semibold text-gray-700 mb-1">
                                                For Age Pension and care fee estimates
                                            </p>
                                            <p className="text-sm text-gray-600 mb-3">
                                                What percentage of this arrangement does Centrelink count toward each person?
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <PercentageInput
                                                    label={
                                                        <span>
                                                            Counted toward {isPartnered || voice.isThirdPerson ? clientName : 'you'} (%){' '}
                                                            <InfoIcon text={apportionmentTooltip} />
                                                        </span>
                                                    }
                                                    name="apportionmentClient"
                                                    value={structure.apportionmentClient}
                                                onChange={(v) => {
                                                    updateStructure(structure.id, 'apportionmentClient', v);
                                                    blurApportionment({
                                                        ...structure,
                                                        apportionmentClient: v,
                                                    });
                                                }}
                                                />
                                                {isPartnered && (
                                                    <PercentageInput
                                                        label={
                                                            <span>
                                                                Counted toward {partnerName} (%){' '}
                                                                <InfoIcon text={apportionmentTooltip} />
                                                            </span>
                                                        }
                                                        name="apportionmentPartner"
                                                        value={structure.apportionmentPartner}
                                                onChange={(v) => {
                                                    updateStructure(structure.id, 'apportionmentPartner', v);
                                                    blurApportionment({
                                                        ...structure,
                                                        apportionmentPartner: v,
                                                    });
                                                }}
                                                    />
                                                )}
                                            </div>
                                            {apportionmentError && (
                                                <p className="text-sm text-red-600 mt-2">{apportionmentError}</p>
                                            )}
                                            {bothZero && !apportionmentError && (
                                                <div className="flex gap-2 p-2 mt-2 rounded bg-white border border-slate-200 border-l-4 border-l-amber-400 text-xs text-slate-700">
                                                    <AlertTriangle className="shrink-0" size={14} />
                                                    <span>
                                                        This arrangement won&apos;t affect pension or care fee estimates until you enter a percentage above 0%.
                                                    </span>
                                                </div>
                                            )}
                                            {(() => {
                                                const remainder = getApportionmentRemainderPercent(structure as unknown as ClientStructure, isPartnered);
                                                if (remainder <= 0 || apportionmentError) return null;
                                                const remainderLabel = Number.isInteger(remainder)
                                                    ? String(remainder)
                                                    : remainder.toFixed(1).replace(/\.0$/, '');
                                                return (
                                                    <p className="text-sm text-gray-600 mt-3 leading-relaxed">
                                                        The remaining <strong>{remainderLabel}%</strong> is treated as belonging to
                                                        other family members (for example children). When you add assets owned by this
                                                        trust or company, enter the{' '}
                                                        <strong>full value</strong> of each asset — we apply the percentages above for
                                                        {' '}{voice.possessive} net worth and estimates.
                                                    </p>
                                                );
                                            })()}
                                            <p className="text-sm text-gray-600 mt-3 leading-relaxed">
                                                Where you link assets to this entity, we have assumed the income from
                                                the asset will be apportioned as above.
                                            </p>
                                        </div>

                                        {showAdviserFields && (
                                            <>
                                                <CurrencyInput
                                                    label={
                                                        <span>
                                                            Override — actual distributions ($ p.a.){' '}
                                                            <InfoIcon text={`When set, can replace asset-based cashflow and (if selected below) pension and care fee income estimates. Tax still uses asset income unless ${voice.pronounPossessive} accountant advises otherwise.`} />
                                                        </span>
                                                    }
                                                    name="annualDistributionPa"
                                                    value={structure.annualDistributionPa ?? ''}
                                                    placeholder="Blank = estimate from assets"
                                                    onChange={(v) => updateStructure(structure.id, 'annualDistributionPa', v)}
                                                />
                                                {parseFloat(String(structure.annualDistributionPa)) > 0 && (
                                                    <FormRadioGroup
                                                        label="Use distribution for pension & care fee income test?"
                                                        name="useDistributionForMeansTest"
                                                        value={toYesNo(structure.useDistributionForMeansTest ?? true)}
                                                        onChange={(n, v) => updateStructure(structure.id, n, fromYesNo(v))}
                                                        options={[
                                                            { label: 'Yes (match Centrelink letter)', value: 'yes' },
                                                            { label: 'No — cashflow only', value: 'no' },
                                                        ]}
                                                    />
                                                )}
                                            </>
                                        )}

                                        {showAdviserFields && (
                                            <FormInput
                                                label="Notes (optional)"
                                                name="notes"
                                                value={structure.notes}
                                                placeholder="e.g. From accountant letter dated …"
                                                onChange={(n, v) => updateStructure(structure.id, n, v)}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <button
                            type="button"
                            onClick={addStructure}
                            className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 font-semibold rounded-lg hover:bg-blue-200"
                        >
                            <PlusCircle size={20} /> Add trust or company
                        </button>
                    </div>

                    {homeIsEntity && structures.length > 0 && (
                        <div className="p-4 rounded-lg border-2 border-teal-200 bg-teal-50/50 space-y-3">
                            <h4 className="font-semibold text-gray-800">{voice.isThirdPerson ? `${voice.Possessive} home` : 'Your family home'}</h4>
                            <p className="text-sm text-gray-600">
                                You told us the home is owned by a trust or company. Which one holds the title?
                            </p>
                            <FormSelect
                                label="Home owner on title"
                                name="homeEntityId"
                                value={factFind.home?.entityId || ''}
                                onChange={(n, v) => linkHomeEntity(v)}
                            >
                                <option value="">Select an arrangement…</option>
                                <option value="__unsure__">I&apos;m not sure yet</option>
                                {structures.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name?.trim() || 'Unnamed arrangement'}
                                    </option>
                                ))}
                            </FormSelect>
                            {factFind.home?.entityId && factFind.home.entityId !== '__unsure__' && (
                                <p className="text-sm text-teal-800">
                                    ✓ {formatCurrency(parseFloat(String(factFind.home?.value)) || 0)} home included under{' '}
                                    {structures.find((s) => s.id === factFind.home?.entityId)?.name?.trim() ||
                                        'selected arrangement'}
                                </p>
                            )}
                            {factFind.home?.entityId === '__unsure__' && (
                                <p className="text-sm text-amber-800">
                                    We&apos;ll show {voice.possessive} home separately until you link it to an arrangement. Pension and care fee estimates may be incomplete.
                                </p>
                            )}
                            {homeIsEntity && !factFind.home?.entityId && (
                                <div className="flex gap-2 text-sm text-slate-700">
                                    <AlertTriangle className="shrink-0" size={16} />
                                    <span>Link {voice.possessive} home to a trust or company so we can include it correctly.</span>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            )}
        </fieldset>
    );
};

export default ClientStructuresSection;
